import React from 'react';
import styles from './index.module.scss';

const PrivacyPolicyPage = () => (
  <main className={styles.container}>
    <h1>Privacy Policy</h1>
    <p>
      Please review this Privacy Policy as it contains important information
      about the processing of your personal data (as defined below).
    </p>

    <section>
      <h2>1. You and Us</h2>
      <p>
        This notice (&quot;Privacy Policy&quot;) is between you and us.
        &quot;You&quot; are an end user or visitor of BF Island mobile
        application, services, content, features, and properties (collectively,
        the &quot;BF Island&quot;). When we mention &quot;BuzzFeed&quot;,
        &quot;we&quot;, &quot;us&quot; or &quot;our&quot; in this Privacy
        Policy, we mean BuzzFeed, Inc., or the relevant BuzzFeed company
        responsible for providing BF Island. BuzzFeed legal entities are listed
        in our Section 13.
      </p>
      <p>
        BF Island is not directed to individuals under 13 years of age, and we
        do not knowingly collect personal data from anyone under 13. If you are
        a parent or guardian and believe your child may have provided us with
        personal data, please contact{' '}
        <a href="mailto:data-privacy@buzzfeed.com">data-privacy@buzzfeed.com</a>{' '}
        and we will delete the information promptly.
      </p>
    </section>

    <section>
      <h2>2. About this Privacy Policy</h2>
      <p>
        This Privacy Policy explains how BuzzFeed collects, uses, discloses, or
        otherwise processes your personal data when you use BF Island. Your
        &quot;personal data&quot; is any information that relates to and
        identifies or is capable of identifying you, directly or indirectly,
        such as your name, address, e-mail address, online identifier, and other
        information that is not otherwise publicly available.
        &quot;Processing&quot; means any operation or set of operations which
        can be performed on personal data.
      </p>
    </section>

    <section>
      <h2>3. Changes to this Privacy Policy</h2>
      <p>
        We&rsquo;re likely to update this Privacy Policy in the future and when
        we make changes, we will revise the date at the top of the policy. When
        we make significant changes, we will notify you through BF Island, via
        email, or other communication. Please come back and check this page from
        time to time for the latest information on our privacy practices.
      </p>
    </section>

    <section>
      <h2>4. How to Contact us</h2>
      <p>
        If you have questions or concerns about the information in this Privacy
        Policy, our handling of your personal data more generally, your choices
        and rights regarding such use, or wish to exercise your rights under
        law, please do not hesitate to contact us at:
      </p>
      <address>
        BuzzFeed Media Enterprises, Inc.
        <br />
        50 W 23rd Street 6th Floor
        <br />
        New York, NY 10010
        <br />
        Attn: Legal Department
        <br />
        <a href="mailto:data-privacy@buzzfeed.com">data-privacy@buzzfeed.com</a>
      </address>
    </section>

    <section>
      <h2>5. How We Collect Your Data</h2>
      <div>
        <h3>Information you Provide Us</h3>
        <p>
          When you create an Account on BF Island, we collect information you
          provide to us. Some of the information you provide to us may be your
          personal data such as your name, username, email address and birthday.
          When you set up your profile, such information, in whole or part, may
          also become your personal data depending on applicable laws. Please
          see our <a href="/user-agreement">User Agreement</a> on Selfies for
          more information.
        </p>
      </div>

      <div>
        <h3>Information We Collect</h3>
        <p>
          When you access or use BF Island, we may collect information about you
          or your activity that may be considered personal data under applicable
          law.
        </p>
        <ul>
          <li>
            Identifier and Inference Information. We collect information about
            how you interact with BF Island and other BF Island users. We
            collect and process information about your Account, including your
            email and user name and use that information, together with other
            information that BuzzFeed may have to make inferences about you.
          </li>
          <li>
            Device Information. We collect information about the device you use
            to access BF Island, including the hardware model, software,
            operating system version, device memory, device identifiers, browser
            type.
          </li>
          <li>
            Location Information. We may collect location information in the
            nature of generalized geographic data using your IP address. This
            will allow us to know what laws may be applicable to you.
          </li>
          <li>
            Online Tracking Information. We may use technologies such as
            cookies, pixels, and software development kits depending on your
            settings.
          </li>
        </ul>
      </div>

      <div>
        <h3>Information We Receive from Third Parties</h3>
        <p>We may receive information about you from third parties. </p>
        <p>
          If you create an account on BF Island using your Apple or Google
          credentials, we will automatically collect certain information, such
          as your Apple or Google user ID. We will use any information collected
          from Apple or Google, including the user ID Apple or Google attribute
          to you, only in accordance with Apple&rsquo;s or Google&rsquo;s
          policies and your preferences selected through Apple or Google.
        </p>
        <p>
          Our partners who provide advertising and related services (such as
          analytics providers and ad networks, our &quot;Advertising
          Partners&quot; may give us your personal data so that we can compare
          it to personal data we hold in order to determine if you are the same
          individual (this is sometimes called &quot;cookie-matching&quot;).
        </p>
      </div>
    </section>

    <section>
      <h2>6. How We Use Your Data</h2>
      <p>
        Overall, we collect your data to give you a better experience with BF
        Island. In detail, we use your data to:
      </p>
      <ul>
        <li>Enable the creation and maintenance of your Account</li>
        <li>
          Verify your age and determine the applicability of laws and regulatory
          requirements to you as a data subject;
        </li>
        <li>
          Identify which legal protections, rights, and obligations apply to you
          under applicable law, and to determine the functionalities, features,
          and activities that may be made available to you, or restricted, on BF
          Island;
        </li>
        <li>
          Personalize and improve BF Island and provide you with advertisements,
          content or features that match your profile, activities or interests;
        </li>
        <li>
          Infer your interests to suggest or recommend (i) features or content
          on BF Island, (ii) other BF Islands users you may like to connect
          with, or (iii) present relevant ads;
        </li>
        <li>
          Send you technical notices, updates, security alerts and support and
          administrative messages and to respond to your comments, questions and
          customer service requests;
        </li>
        <li>
          Communicate with you about products, services, offers, and events
          offered by BuzzFeed and others, and provide news and information we
          think will be of interest to you;
        </li>
        <li>
          Make inferences about your interests and preferences (including at a
          household level). Your interests and preferences are inferred from
          data we collect about the Media and Content you have interacted with,
          how long you interacted with them, how often you interacted with them,
          and the nature of your interaction;
        </li>
        <li>
          Customize content that our third-party Advertising Partners deliver on
          BF Island (such as personalized third-party advertising);
        </li>
        <li>
          Create and update profiles and audience segments that can be used for
          analytics, interest-based advertising and marketing on BF Island, and
          on websites, mobile apps and other platforms controlled by our
          advertisers and other third party advertising partners (&quot;Third
          Party Sites&quot;)
        </li>
        <li>
          We may use information about you, such as your email address, to
          identify you across the devices you use to access BF Island or other
          BuzzFeed offerings. This helps us learn more about your interests and
          preferences, which we use for advertising on BF Island and Third Party
          Sites.
        </li>
        <li>
          Facilitate contests, sweepstakes and promotions and process and
          deliver entries and rewards;
        </li>
        <li>
          Monitor and analyze trends, usage and activities in connection with BF
          Island;
        </li>
        <li>
          Detect, investigate and prevent fraudulent transactions and other
          illegal activities and protect the rights and property of BuzzFeed and
          others; and
        </li>
        <li>
          Carry out any other purpose described to you at the time the data was
          collected.
        </li>
      </ul>
    </section>

    <section>
      <h2>7. Sharing Your Personal Data</h2>
      <p>
        In general, your personal data will remain with BuzzFeed but we may
        share personal data about you as follows or as otherwise described in
        this Privacy Policy:
      </p>
      <ul>
        <li>
          Within BuzzFeed. We share your personal data within BuzzFeed&rsquo;s
          legal entities where necessary to fulfill a contract or precontractual
          measures, or where we have a legitimate interest in doing so (such as
          advertising/marketing) or a legal obligation to do so.
          BuzzFeed&rsquo;s legal entities are listed in our &quot;BuzzFeed Legal
          Entities&quot; in Section 13;
        </li>
        <li>
          With vendors, consultants, and other service providers who process
          your data on our behalf when they provide services to us, for example
          data analytics, research, marketing and financial services;
        </li>
        <li>
          With Advertising Partners to customize your experience and better
          tailor BF Island and Third Party Sites, for marketing and advertising
          purposes. Our Advertising Partners are companies that provide services
          that help us provide advertising to you. These companies may use their
          own tracking technologies to collect or request information about you.
          For more information, please see our &quot;Cookie Policy&quot; in
          Section 11.
        </li>
        <li>
          In connection with, or during negotiations of, any merger, sale of
          company assets, financing or acquisition of all or a portion of our
          business by another company;
        </li>
        <li>
          We may be legally required to disclose or share your personal data
          without your consent in some circumstances, for example to comply with
          a court order or law enforcement. In such circumstances, we will only
          disclose your personal data if we have a good-faith belief that such
          sharing is required under applicable legal obligations;
        </li>
      </ul>
      <p>Sharing of Aggregated or De-Identified Information</p>
      <p>
        We may also share aggregated or de-identified information, which cannot
        reasonably be used to identify you (we do not attempt to re-identify
        de-identified information, except as permitted or required by law). For
        example, we may share aggregated or de-identified information with our
        ad partners and service providers so that we, and they, can understand
        how people use BF Island; this helps us grow our business and improve
        and develop BF Island.
      </p>
      <p>
        We may also share information about you along with a hashed version of
        your email address with our Advertising Partners so that they and we can
        facilitate customized ad campaigns and other offers on BF Island and on
        other platforms. &quot;Hashing&quot; transforms your email into an
        unrecognizable series of numbers and letters (e.g. a &quot;hashed&quot;
        version of jane@email.com would be k5f7g23@email.com).
      </p>
    </section>

    <section>
      <h2>8. Retaining Your Information</h2>
      <p>
        We keep your personal data for as long as we need it for the purposes
        for which it was collected.
      </p>
      <p>
        When we decide how long we will keep your information we consider the
        amount, nature, and sensitivity of the personal data, the potential risk
        of harm from unauthorized use or disclosure, why we need it, and any
        relevant legal requirements (such as legal retention and destruction
        periods). Here are some more factors we consider when we decide how long
        to keep your information:
      </p>
      <ul>
        <li>
          If we need the information to operate or provide BF Island. For
          example, we store your basic Account details — like your user name and
          email address — to maintain your Account.
        </li>
        <li>
          To do the things you expect from BF Island. For example, if you delete
          your Account, we will delete your personal data. However your
          Artifacts will remain.
        </li>
        <li>
          How long we need to retain the information to comply with certain
          legal obligations.
        </li>
        <li>
          If we need it for other legitimate purposes, such as to prevent harm,
          investigate possible violations of our User Agreement or other
          policies, investigate reports of abuse, or protect ourselves or
          others.
        </li>
      </ul>
      <p>
        We may anonymize your personal data (so that it can no longer be
        associated with you) for research or statistical purposes.
      </p>
    </section>

    <section>
      <h2>9. Facial/Image Data/Self-Photograph (&quot;Selfie&quot;)</h2>
      <p>
        When using BF Island, you may voluntarily take and upload a Selfie to
        the platform (&quot;Raw Selfie&quot;). A Raw Selfie may constitute
        personal information because it can contain identifiers—such as facial
        features or other distinguishing characteristics—that can be used to
        identify you, either on their own or when combined with other
        information. Uploading a Raw Selfie is entirely optional and is not
        required to access or use BF Island or any of its features. We do not
        retain, use, or disclose any Raw Selfie for any purpose other than
        solely for the purpose for which it was provided.
      </p>
    </section>

    <section>
      <h2>10. Notice for California Users</h2>
      <p>
        This section provides you with our practices regarding the collection,
        use, and disclosure of your personal data. It also explains your rights
        regarding your personal data that we collect pursuant to the California
        Privacy Rights Act, its amendments, and regulations (collectively the
        &quot;CCPA&quot;).
      </p>
      <h4>Categories of Information We Collect:</h4>
      <ul>
        <li>
          Your identifying details (e.g. your email address, Google/Apple ID,
          birthday and username).
        </li>
        <li>
          Inferences (e.g. inferences about your personal preferences and
          attributes drawn from profiling).
        </li>
        <li>
          Online identifiers (e.g. IP address, ADID/IDFA, cookie IDs, internal
          identification numbers, Google/Apple ID,).
        </li>
        <li>Location data (e.g. your city, state or country information).</li>
      </ul>
      <h4>Categories of Sources:</h4>
      <ul>
        <li>You</li>
        <li>Your device</li>
        <li>Data partners, such as Apple and Google.</li>
      </ul>
      <h4>Business or Commercial Purposes for Collection</h4>
      <ul>
        <li>
          Auditing related to counting ad impressions to unique visitors,
          verifying positioning and quality of ad impressions, and auditing
          compliance with this specification and other standards.
        </li>
        <li>Ensure security and integrity.</li>
        <li>
          Debugging to identify and repair errors that impair existing intended
          functionality.
        </li>
        <li>
          Short-term, transient use, including, but not limited to,
          nonpersonalized advertising shown as part of your interaction with BF
          Island.
        </li>
        <li>
          Performing services on behalf of BuzzFeed, including maintaining or
          servicing Accounts, providing customer service, verifying your
          information, providing analytic services, providing storage, or
          providing similar services on behalf of BuzzFeed.
        </li>
        <li>Providing advertising and marketing services.</li>
        <li>
          Undertaking internal research for technological development and
          demonstration.
        </li>
        <li>
          Undertaking activities to verify or maintain the quality or safety of
          BF Island, and to improve, upgrade, or enhance BF Island.
        </li>
      </ul>
      <h4>When We Sell or Share Personal Data</h4>
      <table>
        <tbody>
          <tr>
            <td>
              <strong>Categories of data sold or shared:</strong>
            </td>
            <td>
              <strong>
                Categories of Vendors or Other Entities We Sell/Share data With:
              </strong>
            </td>
          </tr>
          <tr>
            <td>Your contact details</td>
            <td>Social network partners and our business partners.</td>
          </tr>
          <tr>
            <td>
              Internet activity information, inferences and online identifiers
            </td>
            <td>
              Advertising networks, data analytics vendors, social networks, and
              other Advertising Partners
            </td>
          </tr>
          <tr>
            <td>
              <strong>Categories of data disclosed:</strong>
            </td>
            <td>
              <strong>
                Categories of Vendors or Other Entities We disclose data to:
              </strong>
            </td>
          </tr>
          <tr>
            <td>Your contact details</td>
            <td>
              Our email service vendors, sweepstakes/promotions partners, social
              network partners, other vendors who assist in providing services
              on our behalf, and our business partners.
            </td>
          </tr>
          <tr>
            <td>
              Internet activity information, inferences and online identifiers
            </td>
            <td>
              Advertising networks, internet service providers, data analytics
              vendors, operating systems and platforms, and social networks.
            </td>
          </tr>
          <tr>
            <td>Location data</td>
            <td>
              Internet service providers, data analytics vendors, operating
              systems, and platforms.
            </td>
          </tr>
        </tbody>
      </table>
      <p>
        We may share your personal data with government entities when required
        to do by law.
      </p>

      <h3>California Consumer Data Privacy Rights</h3>
      <p>Your California Consumer privacy rights are as follows:</p>
      <ol className={styles.upperAlpha}>
        <li>
          <p>The Right to Know and Have Access to Data</p>
          <p>
            You have the right to obtain a copy of the specific pieces of
            personal data that we have collected about you. To exercise your
            right, please contact us at{' '}
            <a href="mailto:data-privacy@buzzfeed.com">
              data-privacy@buzzfeed.com
            </a>
            .
          </p>
        </li>
        <li>
          <p>Right to Delete</p>
          <p>
            You may request that we delete your personal data that we have
            collected from you. Please note that even if you make a request to
            delete your personal data, applicable law may permit or require us
            to retain personal data that was collected for security purposes,
            for legitimate internal business purposes, including maintaining
            business records, to comply with law, to exercise or defend legal
            claims, and to cooperate with law enforcement. To exercise your
            right, please contact us at{' '}
            <a href="mailto:data-privacy@buzzfeed.com">
              data-privacy@buzzfeed.com
            </a>
            .
          </p>
        </li>
        <li>
          <p> Right to Correct</p>
          <p>
            You may request that we correct the personal data that we maintain
            about you that is inaccurate. To exercise your right, please contact
            us at{' '}
            <a href="mailto:data-privacy@buzzfeed.com">
              data-privacy@buzzfeed.com
            </a>
            .
          </p>
        </li>
        <li>
          <p>Right to Opt Out of Sale or Sharing</p>
          <p>
            You may opt out of &quot;sales&quot; or &quot;sharing&quot; of
            personal data (as defined under applicable law) as follows:
          </p>
          <ul>
            <li>
              Click on &quot;Cookie Settings&quot; and deactivate cookies.
            </li>
            <li>Contact us at data-privacy@buzzfeed.com.</li>
            <li>
              Exercising opt-out choices offered by the Digital Advertising
              Alliance at http://www.aboutads.info/choices, or by the Network
              Advertising Initiative at
              http://www.networkadvertising.org/choices/.
            </li>
          </ul>
        </li>
        <li>
          <p>No Right to Limit.</p>
          <p>
            BuzzFeed does not use or disclose sensitive personal data for
            purposes that, under applicable law, require us to offer consumers a
            &quot;Right to Limit&quot; the use or disclosure of sensitive
            personal data.
          </p>
        </li>
        <li>
          <p>Non-Discrimination</p>
          <p>
            As required by the CCPA, we will not discriminate against you
            because you exercise your CCPA rights.
          </p>
        </li>
      </ol>

      <h3>California Shine the Light Disclosure</h3>
      <p>
        Under California Civil Code sections 1798.83- 1798.84, California
        residents are entitled to ask for a notice identifying the categories of
        personal customer data which we share with our affiliates and/or third
        parties for their own direct marketing purposes, and providing contact
        information for such affiliates and/or third parties. If you are a
        California resident and would like a copy of this notice, please submit
        a written request to the following address: 50 West 23rd Street, New
        York, NY 10010, 6th Floor, Attn: Legal Department.
      </p>
    </section>

    <section>
      <h2>11. Cookie Information</h2>
      <p>
        We may use cookies or similar technologies to improve BF Island
        performance and user experience. These cookies are small data files
        stored on your device and are used for limited technical and analytical
        purposes. We may use Google Analytics to help us understand overall
        trends, such as session length, crash frequency, or device types. Google
        Analytics uses technical data such as device type and approximate
        location to generate aggregated reports. This information is processed
        in such a way that does not identify any individual user, and we do not
        receive or store any personal data from Google Analytics.
      </p>
      <p>
        Cookies and similar technologies allow us to collect the following types
        of personal data: a unique ID assigned to your device; IP address;
        device and browser type; operating system; content viewed or other
        actions taken through BF Island; time and date of those actions; country
        and language selected.
      </p>
      <p>We use three types of cookies:</p>
      <ul>
        <li>
          <strong>Necessary Cookies</strong> - These cookies are essential for
          BF Island to function properly. They enable features such as saving
          your settings or maintaining sessions.
        </li>
        <li>
          <strong>Session Cookies</strong> - These cookies remember you during
          your session on BF Island. They are deleted when you log out of BF
          Island. We use session cookies to ensure content is displayed
          correctly on BF Island, to balance and monitor load on our servers,
          and for other technical matters essential to the operation of BF
          Island.
        </li>
        <li>
          <strong>Analytics Cookies</strong> - These cookies help us understand
          how visitors interact with BF Island, such as which features are most
          used or how long sessions last. This information helps us improve BF
          Island&rsquo;s performance and design.
        </li>
      </ul>
      <p>
        We use Google Analytics to measure traffic on our website. Google has
        their own Privacy Policy which you can review{' '}
        <a href="https://support.google.com/analytics/answer/181881?hl=en">
          here
        </a>
        . If you&rsquo;d like to opt out of tracking by Google Analytics, visit
        the Google Analytics opt-out page.
      </p>
    </section>

    <section>
      <h2>12. Privacy Information for All Users</h2>
      <p>
        As described above, depending on your place of residence, you may have
        certain data privacy rights afforded by law; however, we allow you to
        exercise select privacy rights regardless of your jurisdiction.
      </p>
      <p>
        To request deletion of your personal data or request access to the
        personal data we may have about you, please email us at{' '}
        <a href="mailto:data-privacy@buzzfeed.com">data-privacy@buzzfeed.com</a>
        .
      </p>
    </section>

    <section>
      <h2>13. BuzzFeed legal entities</h2>
      <ul>
        <li>BuzzFeed, Inc.</li>
        <li>BuzzFeed Media Enterprises, Inc.</li>
        <li>BuzzFeed Canada, Inc.</li>
        <li>BuzzFeed UK Limited</li>
        <li>BuzzFeed India Private Limited</li>
        <li>BuzzFeed PTY, Ltd (Australia)</li>
        <li>BuzzFeed Japan Corporation</li>
        <li>Entertainment Contents Online Mexico, S.A. de C.V.</li>
        <li>BuzzFeed Motion Pictures Inc.</li>
        <li>Product Labs, Inc.</li>
        <li>Torando Labs, Inc.</li>
        <li>Lexland Studios, Inc (formerly known as BFMP Video, Inc.)</li>
        <li>The HuffingtonPost.com, Inc.</li>
      </ul>
    </section>
    <br />
    <br />
    <section>
      <a href="/user-agreement">User Agreement</a> | <a href="/eula">EULA</a> |{' '}
      <a href="/dmca">DMCA</a>
    </section>
  </main>
);

export default PrivacyPolicyPage;
